import {
    Breadcrumb,
    Button,
    Modal,
    Row,
    Space,
    Tag
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { extractLocalizedValue, getStatusColor } from "../../../common";
import { VerificationStatus, VerificationStep } from "../../../common/constant";
import {
    useAbandonUserVerificationProfile,
    useUpdateUserVerificationHistoryStatus
} from "../../../function/accountVerification";
import { convertToUppercase } from "../../../../../_common/function";
import { isMobile } from "react-device-detect";
import { apiCancelVerificationHistory } from "../../../../../api/restful/verification";
import { resolveInitialStep, resolveBlockedTier } from "../../../lib/resolveStep";
import SelectCountry from "./steps/SelectCountry";
import SelectFlow from "./steps/SelectFlow";
import SelectTier from "./steps/SelectTier";
import SelectDocument from "./steps/SelectDocument";
import DocumentForm from "./steps/DocumentForm";
import VerificationPending from "./steps/VerificationPending";

export default function AccountVerify_1(props) {
    const {
        userVerificationFlows,
        getUserVerificationFlows,
        userVerificationProfile,
        refetchUserVerificationProfile,
        userVerificationTiers,
        refetchUserVerificationTiers,
        userVerificationHistories,
        refetchUserVerificationHistories,
        accessRightsList
    } = props

    const { t } = useTranslation();

    const initialStep = userVerificationProfile?.flowSetting ? VerificationStep.Tier : VerificationStep.Flow;

    const { abandonUserVerificationProfile, loading: abandonLoading } = useAbandonUserVerificationProfile();
    const {
        updateUserVerificationHistoryStatus,
        loading: updateHistoryStatusLoading
    } = useUpdateUserVerificationHistoryStatus();

    const [currentStep, setCurrentStep] = useState(initialStep);
    const [selectedVerificationType, setSelectedVerificationType] = useState(null);
    const selectedFlow = userVerificationFlows?.find(flow => flow._id === userVerificationProfile?.flowSetting) || null;
    const [selectedTier, setSelectedTier] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedInnerDocument, setSelectedInnerDocument] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(userVerificationProfile?.country)
    const [cancelOpen, setCancelOpen] = useState(false);

    const sortedHistories = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    const currentHistory = sortedHistories.find((history) => (history.tierSetting?._id || history.tierSetting) === selectedTier?._id)
    const tierLevelArray = (userVerificationTiers || []).map(d => d?.tierLevel).filter(Number.isFinite)
    const tierSettingNextTierExist = Number.isFinite(sortedHistories?.[0]?.tierLevel) && sortedHistories[0].tierLevel < (tierLevelArray.length ? Math.max(...tierLevelArray) : 0)
    const { blockedByFailedTier, failedTierHistory, blockedTierHistory } = resolveBlockedTier(userVerificationHistories)

    const goNext = () => setCurrentStep(prev => prev + 1);
    const goTo = (step) => setCurrentStep(step);
    const goBack = () => setCurrentStep(prev => prev - 1);

    const handleSelectVerificationType = (type) => {
        setSelectedVerificationType(type);
    }

    const handleStartVerification = async () => {
        await Promise.all([
            refetchUserVerificationTiers(),
            refetchUserVerificationProfile(),
        ]);
    }

    const handleCancelFlow = () => {
        setCancelOpen(true)
    }

    const handleResubmitVerification = async () => {

        if (!currentHistory?._id || currentHistory.status !== VerificationStatus.PENDING) return;

        const payload = {
            _id: currentHistory._id,
            status: VerificationStatus.DRAFT
        }

        await updateUserVerificationHistoryStatus(payload)
        await Promise.all([
            refetchUserVerificationHistories(),
        ])
    }

    const buildBreadcrumbItems = () => {
        const items = [];

        if (selectedFlow) {
            items.push({
                title: extractLocalizedValue(selectedFlow?.displayName) || selectedFlow?.name,
            });
        }

        if (currentStep > VerificationStep.Tier && selectedTier) {

            if (currentStep === VerificationStep.Document) {
                const tierRequired = selectedTier?.numberOfRequired;
                const documentsRequired = selectedTier?.documentSettings?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name
                let message = ""

                if (tierRequired === selectedTier?.documentSettings?.length || tierRequired === documentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (tierRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", { numberOfRequired: tierRequired });
                } else {
                    message = ""
                }

                if (message) {
                    title = (
                        <span>
                            {title} (<strong>{message}</strong>)
                        </span>
                    );
                }

                items.push({ title });
            } else {
                items.push({ title: extractLocalizedValue(selectedTier?.displayName) || selectedTier?.name, });
            }
        }

        if (currentStep > VerificationStep.Document && selectedDocument) {

            if (currentStep === VerificationStep.DocumentGroup) {
                const documentRequired = selectedDocument?.numberOfRequired;
                const innerDocumentsRequired = selectedDocument?.steps?.filter(doc => doc?.isRequired)?.length

                let title = extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name
                let message = ""

                if (documentRequired === selectedDocument?.steps?.length || documentRequired === innerDocumentsRequired) {
                    message = t("accountVerification.pleaseCompleteAllSections");
                } else if (documentRequired > 0) {
                    message = t("accountVerification.pleaseCompleteAnySections", { numberOfRequired: documentRequired });
                } else {
                    message = ""
                }

                if (message) {
                    title = (
                        <span>
                            {title} (<strong>{message}</strong>)
                        </span>
                    );
                }

                items.push({ title });
            } else if (selectedInnerDocument) {
                items.push({ title: extractLocalizedValue(selectedDocument?.displayName) || selectedDocument?.name, });
            }
        }

        if (currentStep > VerificationStep.DocumentGroup && selectedDocument) {
            let title = extractLocalizedValue(selectedInnerDocument?.displayName || selectedDocument?.displayName) || selectedInnerDocument?.name || selectedDocument?.name;

            const verificationStatus = currentHistory?.status;

            items.push({
                title: (
                    <span>
                        {title}
                        {verificationStatus !== VerificationStatus.DRAFT && (
                            <Tag
                                color={getStatusColor(verificationStatus)}
                                style={{ marginLeft: 6 }}
                            >
                                {convertToUppercase(verificationStatus.replace(/_/g, " "))}
                            </Tag>
                        )}
                    </span>
                ),
            });
        }

        return items;
    }

    useEffect(() => {
        const { step, selectedTierId } = resolveInitialStep({
            profile: userVerificationProfile,
            tiers: userVerificationTiers,
            histories: userVerificationHistories,
        });
        if (selectedTierId) setSelectedTier(userVerificationTiers?.find(d => d?._id === selectedTierId));
        setCurrentStep(step);
        // deps intentionally [profile, tiers] only — refetch after save/submit changes
        // histories, and re-firing here would eject the user from the form mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userVerificationProfile, userVerificationTiers])

    const breadcrumbItems = buildBreadcrumbItems(
        currentStep,
        selectedFlow,
        selectedTier,
        selectedDocument,
        extractLocalizedValue
    );

    return (
        <div id={"accountVerify"}>
            <Row justify={"center"} style={{ marginBottom: "60px" }}>
                <div className={"w-full"}>
                    <Space style={{ width: "100%" }} size={24} direction="vertical" className={"w-full"}>

                        {
                            currentStep > VerificationStep.Flow && currentStep < VerificationStep.Status && (
                                <div className="breadcrumb-header">
                                    <div className="breadcrumb-container">
                                        <Breadcrumb
                                            items={breadcrumbItems}
                                        />
                                    </div>

                                    <div className="breadcrumb-action">
                                        {currentStep >= VerificationStep.Tier && currentHistory?.status === "draft_verification" && (
                                            <Button
                                                className="breadcrumb-action abandon-btn"
                                                onClick={handleCancelFlow}
                                            >
                                                {t("common.cancel")}
                                            </Button>
                                        )}

                                        {currentStep === VerificationStep.Document &&
                                            currentHistory?.status === VerificationStatus.PENDING && (
                                                <Button
                                                    danger
                                                    className="breadcrumb-action"
                                                    onClick={handleResubmitVerification}
                                                >
                                                    {t("accountVerification.resubmitDocument")}
                                                </Button>
                                            )
                                        }

                                    </div>

                                </div>
                            )
                        }

                        {
                            currentStep === VerificationStep.Country
                            &&
                            (
                                <SelectCountry
                                    selectedCountry={selectedCountry}
                                    setSelectedCountry={setSelectedCountry}
                                    userVerificationProfile={userVerificationProfile}
                                    goNext={goNext}
                                    getUserVerificationFlows={getUserVerificationFlows}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Flow
                            &&
                            (
                                <SelectFlow
                                    userVerificationFlows={userVerificationFlows}
                                    selectedVerificationType={selectedVerificationType}
                                    handleSelectVerificationType={handleSelectVerificationType}
                                    handleStartVerification={handleStartVerification}
                                    goBack={goBack}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Tier
                            &&
                            (
                                <SelectTier
                                    userVerificationHistories={userVerificationHistories}
                                    userVerificationTiers={userVerificationTiers}
                                    refetchUserVerificationProfile={refetchUserVerificationProfile}
                                    goTo={goTo}
                                    setSelectedTier={setSelectedTier}
                                    accessRightsListCategory={accessRightsList}
                                    blockedByFailedTier={blockedByFailedTier}
                                    failedTierHistory={failedTierHistory}
                                    blockedTierHistory={blockedTierHistory}
                                />
                            )
                        }

                        {
                            (currentStep === VerificationStep.Document || currentStep === VerificationStep.DocumentGroup) &&
                            (
                                <SelectDocument
                                    currentHistory={currentHistory}
                                    setSelectedDocument={setSelectedDocument}
                                    setSelectedInnerDocument={setSelectedInnerDocument}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    userVerificationHistories={userVerificationHistories}
                                    goNext={goNext}
                                    goBack={goBack}
                                    goTo={goTo}
                                    isInnerDocument={currentStep === VerificationStep.DocumentGroup}
                                    tierSettingNextTierExist={tierSettingNextTierExist}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Form
                            &&
                            (
                                <DocumentForm
                                    currentHistory={currentHistory}
                                    selectedTier={selectedTier}
                                    selectedDocument={selectedDocument}
                                    selectedInnerDocument={selectedInnerDocument}
                                    goBack={goBack}
                                    goTo={goTo}
                                    refetchUserVerificationHistories={refetchUserVerificationHistories}
                                />
                            )
                        }

                        {
                            currentStep === VerificationStep.Status &&
                            <VerificationPending />
                        }
                        {/* <Row justify="space-between" style={{ marginTop: 24 }}>
                            {currentStep > 0 && (
                                <Button onClick={goBack}>
                                    Back
                                </Button>
                            )}

                            {currentStep < 3 && (
                                <Button type="primary" onClick={goNext}>
                                    Next
                                </Button>
                            )}
                        </Row> */}
                    </Space>
                </div>
            </Row>

            <Modal
                title={"Confirm Cancel"}
                cancelText={t("No, Continue")}
                centered={true}
                open={cancelOpen}
                getContainer={() => document.getElementById("layout-1")}
                width={isMobile ? "90%" : "350px"}
                okButtonProps={{ type: "default" }}
                onOk={async () => {
                    await apiCancelVerificationHistory(currentHistory?._id);
                    await refetchUserVerificationProfile();
                    await refetchUserVerificationHistories();
                    setCancelOpen(false)

                    setSelectedTier(null)
                    setSelectedDocument(null)
                    setSelectedInnerDocument(null)
                    goTo(VerificationStep.Tier)
                }}
                okText={"Yes, Cancel"}
                cancelButtonProps={{ type: "primary" }}
                onCancel={() => setCancelOpen(false)}
            >
                Are you sure you want to cancel this verification process? Your progress will be lost.
            </Modal>
        </div>
    )
}
